import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import GroupsIcon from '@mui/icons-material/Groups'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Fade,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { BarChart } from '@mui/x-charts/BarChart'
import { LineChart } from '@mui/x-charts/LineChart'
import { PieChart } from '@mui/x-charts/PieChart'
import { useEntitiesFromSearch } from 'api/search/useEntitiesFromSearch'
import { useReportsData } from 'api/search/useReportsData'
import { formatCurrency } from 'components/DealsKanban/aggregateStageTotals'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import React, { useMemo, useState } from 'react'
import type { ReportPeriod } from 'utils/reportMetrics'

// ─── Period options ──────────────────────────────────────────────────

const PERIOD_OPTIONS: { value: ReportPeriod; label: string }[] = [
  { value: 'current_month', label: 'This Month' },
  { value: 'current_quarter', label: 'This Quarter' },
  { value: 'ytd', label: 'YTD' },
  { value: 'trailing_12', label: 'Last 12 Months' },
  { value: 'custom', label: 'Custom' },
]

// ─── KPI Card ────────────────────────────────────────────────────────

const KpiCard = ({
  label,
  value,
  hint,
  isLoading,
  prefix,
}: {
  label: string
  value: string | number
  hint?: string
  isLoading?: boolean
  prefix?: React.ReactNode
}) => {
  const theme = useTheme()
  return (
    <Card sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent sx={{ bgcolor: 'background.section', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Stack direction="row" alignItems="center" gap={1}>
            {prefix}
            <Typography variant="overline" sx={{ opacity: 0.7, color: 'text.primary' }}>
              {label}
            </Typography>
          </Stack>
          {hint && (
            <Tooltip title={hint}>
              <IconButton size="small">
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        {isLoading ? (
          <Skeleton variant="text" width="60%" height={40} sx={{ mt: 0.5 }} />
        ) : (
          <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 700, color: 'text.primary' }}>
            {value}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Color palette ───────────────────────────────────────────────────

const CHART_COLORS = [
  'rgb(107, 185, 240)',
  'rgb(80, 200, 120)',
  'rgb(245, 108, 53)',
  'rgb(139, 92, 246)',
  'rgb(247, 63, 134)',
  'rgb(52, 211, 145)',
  'rgb(20, 119, 248)',
  'rgb(251, 193, 30)',
  'rgb(247, 63, 236)',
  'rgb(220, 20, 60)',
]

// ─── Main Page ───────────────────────────────────────────────────────

const ReportsDashboardPage = () => {
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const [period, setPeriod] = useState<ReportPeriod>('current_month')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [selectedOrg, setSelectedOrg] = useState<{ PK: string; SK: string; name: string; chartId: string } | null>(null)

  const { organizations } = useEntitiesFromSearch()
  const orgOptions = useMemo(() => {
    if (!organizations?.length) return []
    return organizations
      .filter(o => o?.name)
      .map(o => ({ PK: o.PK, SK: o.SK, name: o.name, chartId: `${o.PK}::${o.SK}` }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [organizations])

  const { data, isLoading } = useReportsData({
    period,
    startDate: period === 'custom' ? startDate : undefined,
    endDate: period === 'custom' ? endDate : undefined,
    orgId: selectedOrg?.PK,
  })

  const summary = data?.summary
  const trend = data?.trend ?? []
  const byService = data?.byService ?? []
  const byOrganization = data?.byOrganization ?? []

  // Prepare pie chart data (top 8, rest grouped as "Other")
  const pieData = useMemo(() => {
    if (!byService.length) return []
    const top = byService.slice(0, 8)
    const rest = byService.slice(8)
    const items = top.map((s, i) => ({
      id: i,
      value: s.monthlyRevenue,
      label: s.service,
      color: CHART_COLORS[i % CHART_COLORS.length],
    }))
    if (rest.length > 0) {
      const otherTotal = rest.reduce((sum, s) => sum + s.monthlyRevenue, 0)
      items.push({ id: items.length, value: otherTotal, label: 'Other', color: 'rgb(160, 160, 160)' })
    }
    return items
  }, [byService])

  // Top 10 orgs for bar chart — deduplicate names so MUI Charts keys don't collide
  const topOrgs = useMemo(() => {
    const sliced = byOrganization.slice(0, 10)
    const nameCounts: Record<string, number> = {}
    return sliced.map(o => {
      const count = (nameCounts[o.orgName] = (nameCounts[o.orgName] ?? 0) + 1)
      return count > 1 ? { ...o, orgName: `${o.orgName} (${count})` } : o
    })
  }, [byOrganization])
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        overflowY: 'auto',
        maxHeight: pinned ? 'calc(100vh - 5px)' : '100vh',
      }}
    >
      {/* ── Period Selector Bar ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          pt: pinned ? '50px' : 0,
          transition: 'padding 0.3s ease',
        }}
      >
        <TrendingUpIcon sx={{ color: 'rgb(139, 92, 246)', fontSize: 28 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, mr: 2 }}>
          Reports
        </Typography>

        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(_, val) => val && setPeriod(val)}
          size="small"
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2 } }}
        >
          {PERIOD_OPTIONS.map(opt => (
            <ToggleButton key={opt.value} value={opt.value}>
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {period === 'custom' && (
          <>
            <TextField
              type="date"
              size="small"
              label="Start"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
            />
            <TextField
              type="date"
              size="small"
              label="End"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
            />
          </>
        )}

        <Autocomplete
          options={orgOptions}
          getOptionLabel={o => o.name}
          getOptionKey={o => o.chartId}
          value={selectedOrg}
          onChange={(_, val) => setSelectedOrg(val)}
          size="small"
          sx={{ minWidth: 240 }}
          renderInput={params => <TextField {...params} label="Filter by Organization" />}
        />
      </Box>

      {/* ── KPI Cards ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            label="Monthly Revenue"
            value={summary ? formatCurrency(summary.totalMonthlyRevenue) : '-'}
            hint="Sum of all active product prices in the selected period"
            isLoading={isLoading}
            prefix={<AttachMoneyIcon sx={{ fontSize: 18, color: 'rgb(80, 200, 120)' }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            label="Gross Profit"
            value={summary ? formatCurrency(summary.totalGrossProfit) : '-'}
            hint="Revenue × margin% for each product"
            isLoading={isLoading}
            prefix={<AttachMoneyIcon sx={{ fontSize: 18, color: 'rgb(107, 185, 240)' }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            label="GRO Media Spend"
            value={summary ? formatCurrency(summary.totalGroMediaSpend) : '-'}
            hint="Media spend on GRO-paid products"
            isLoading={isLoading}
            prefix={<AttachMoneyIcon sx={{ fontSize: 18, color: 'rgb(245, 108, 53)' }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            label="Client Media Spend"
            value={summary ? formatCurrency(summary.totalClientMediaSpend) : '-'}
            hint="Media spend on Client-paid products"
            isLoading={isLoading}
            prefix={<AttachMoneyIcon sx={{ fontSize: 18, color: 'rgb(139, 92, 246)' }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            label="Active Deals"
            value={summary?.activeDealCount ?? '-'}
            hint="Deals with active products in the selected period"
            isLoading={isLoading}
            prefix={<GroupsIcon sx={{ fontSize: 18, color: 'rgb(52, 211, 145)' }} />}
          />
        </Grid>
      </Grid>

      {/* ── Revenue Trend Chart ── */}
      {trend.length > 1 && (
        <Fade in={true} timeout={{ enter: 200, exit: 120 }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={12} md={12}>
              <Card sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent sx={{ bgcolor: 'background.section' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Revenue Trend
                  </Typography>
                  <LineChart
                    height={280}
                    series={[
                      {
                        data: trend.map(t => t.revenue),
                        label: 'Revenue',
                        color: 'rgb(80, 200, 120)',
                        // area: true,
                      },
                      {
                        data: trend.map(t => t.grossProfit),
                        label: 'Gross Profit',
                        color: 'rgb(107, 185, 240)',
                      },
                      {
                        data: trend.map(t => t.groMediaSpend),
                        label: 'GRO Media Spend',
                        color: 'rgb(245, 108, 53)',
                      },
                      {
                        data: trend.map(t => t.clientMediaSpend),
                        label: 'Client Media Spend',
                        color: 'rgb(139, 92, 246)',
                      },
                    ]}
                    xAxis={[
                      {
                        data: trend.map(t => t.month),
                        scaleType: 'point',
                      },
                    ]}
                    yAxis={[
                      {
                        valueFormatter: (v: number | null) => (v != null ? `$${(v / 1000).toFixed(0)}k` : ''),
                      },
                    ]}
                    slotProps={{
                      legend: {
                        direction: 'row',
                        position: { vertical: 'top', horizontal: 'right' },
                      },
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Fade>
      )}

      {/* ── Breakdowns: Service (Pie) + Organization (Bar) ── */}
      <Grid container spacing={2}>
        {/* Revenue by Service */}
        {pieData.length > 0 && (
          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent sx={{ bgcolor: 'background.section' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Revenue by Service
                </Typography>
                <PieChart
                  height={400}
                  series={[
                    {
                      data: pieData,
                      innerRadius: 40,
                      outerRadius: 110,
                      paddingAngle: 2,
                      cornerRadius: 4,
                      highlightScope: { fade: 'global', highlight: 'item' },
                    },
                  ]}
                  slotProps={{
                    legend: {
                      direction: 'column',
                      position: { vertical: 'middle', horizontal: 'right' },
                      itemMarkWidth: 10,
                      itemMarkHeight: 10,
                      labelStyle: { fontSize: 12 },
                    },
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Revenue by Organization */}
        {topOrgs.length > 0 && (
          <Grid item xs={12} md={7}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent sx={{ bgcolor: 'background.section' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Revenue by Organization
                </Typography>
                <BarChart
                  height={400}
                  layout="horizontal"
                  series={[
                    {
                      data: topOrgs.map(o => o.revenue),
                      label: 'Revenue',
                      color: 'rgb(80, 200, 120)',
                    },
                    {
                      data: topOrgs.map(o => o.grossProfit),
                      label: 'Gross Profit',
                      color: 'rgb(107, 185, 240)',
                    },
                  ]}
                  yAxis={[
                    {
                      data: topOrgs.map(o => o.orgName),
                      scaleType: 'band',
                    },
                  ]}
                  xAxis={[
                    {
                      valueFormatter: (v: number | null) => (v != null ? `$${(v / 1000).toFixed(0)}k` : ''),
                    },
                  ]}
                  slotProps={{
                    legend: {
                      direction: 'row',
                      position: { vertical: 'top', horizontal: 'right' },
                    },
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
        {data?.meta && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <Typography
              sx={{
                mt: 'auto',
                pt: 2,
                fontSize: 11,
                color: 'text.secondary',
                textAlign: 'right',
              }}
            >
              Period: {data.meta.startDate} to {data.meta.endDate} | Generated:{' '}
              {new Date(data.meta.generatedAt).toLocaleString()}
            </Typography>
          </Box>
        )}
      </Grid>

      {/* ── Meta info ── */}
      {/* {data?.meta && (
        <Typography sx={{ mt: 'auto', pt: 2, fontSize: 11, color: 'text.secondary', textAlign: 'right' }}>
          Period: {data.meta.startDate} to {data.meta.endDate} | Generated:{' '}
          {new Date(data.meta.generatedAt).toLocaleString()}
        </Typography>
      )} */}
    </Box>
  )
}

export default ReportsDashboardPage
